import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import Layout from './components/layout/Layout';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { ToastProvider } from './contexts/ToastContext';
import { ReadingModeProvider } from './contexts/ReadingModeContext';
import { msalConfig, AuthProvider, ProtectedRoute } from './auth';
import { initializeApiAuth } from './services/api';
import {
  Dashboard,
  InterpretationsList,
  InterpretationDetail,
  TestDataPending,
  Config,
  LoginPage,
  Metrics,
  CostsPage,
} from './pages';
import AdminTotpPage from './pages/AdminTotpPage';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Handle redirect promise to clear any pending interactions
msalInstance.initialize().then(() => {
  // Handle redirect if coming back from login
  msalInstance.handleRedirectPromise().catch((error) => {
    console.error('Redirect error:', error);
  });

  // Set active account if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }
});

// Initialize API authentication with MSAL instance
initializeApiAuth(msalInstance);

// Set active account on successful login
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    msalInstance.setActiveAccount(payload.account);
  }
});

const App: React.FC = () => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <ToastProvider>
          <ReadingModeProvider>
            <BrowserRouter>
              <NotificationsProvider>
                <Routes>
                  {/* Public route - Login */}
                  <Route path="/login" element={<LoginPage />} />

                  {/* Protected routes */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Dashboard />} />
                    <Route path="interpretations" element={<InterpretationsList />} />
                    <Route path="interpretations/:id" element={<InterpretationDetail />} />
                    <Route path="test-data" element={<TestDataPending />} />
                    <Route path="config" element={<Config />} />
                    <Route path="metrics" element={<Metrics />} />
                    <Route path="costs" element={<CostsPage />} />
                    <Route path="admin/totp" element={<AdminTotpPage />} />
                  </Route>
                </Routes>
              </NotificationsProvider>
            </BrowserRouter>
          </ReadingModeProvider>
        </ToastProvider>
      </AuthProvider>
    </MsalProvider>
  );
};

export default App;
